import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSearch,
  faFilterList,
} from "@fortawesome/pro-regular-svg-icons";
import { Link, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Modal from "../../components/Modal";
import Dropdown from "../../components/Dropdown";
import {
  getAccountTypesByAgreement,
  deleteAccountType,
} from "../../api/Agreement";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import TableView from "../../components/TableView";
import PriceFormatter from "../../helpers/PriceFormater";

const AccountPage = () => {
  const { agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const [accountTypes, setAccountTypes] = useState([]);
  const [filteredAccountTypes, setFilteredAccountTypes] = useState([]);
  const [selectedAccountType, setSelectedAccountType] = useState([]);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [accountTypesLoaded, setAccountTypesLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  const handleOpenModal = () => {
    if (!selectedAccountType || selectedAccountType.length === 0) {
        toast.error("No account types selected");
        return;
      }
    setIsModalOpen(true);
  };

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = accountTypes;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = accountTypes.filter(
          (accountType) =>
            accountType.userId?.toLowerCase().includes(searchLower) ||
          accountType.displayName?.toLowerCase().includes(searchLower) ||
          accountType.userPrincipalName?.toLowerCase().includes(searchLower) ||
          accountType.accountType?.toLowerCase().includes(searchLower) ||
          accountType.accountType?.toLowerCase().includes(searchLower)
        );
      }

      if (sortColumn) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = (a[sortColumn] || "").toString().toLowerCase();
          const bValue = (b[sortColumn] || "").toString().toLowerCase();
          return sortOrder === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      setFilteredAccountTypes(filtered);
    };

    applyFilterAndSort();
  }, [search, accountTypes, sortColumn, sortOrder]);

  useEffect(() => {
    const fetchAccountTypes = async () => {
        console.log("FETCHING ACCOUNT TYPES");
      try {
        const accessToken = await getAccessToken();
        const groups = await getAccountTypesByAgreement(agreementPublicId, accessToken);

        groups.forEach(group => {
          group.price = PriceFormatter(group.price);

        });

        setAccountTypes(groups);
        setFilteredAccountTypes(groups);
        setAccountTypesLoaded(true); 
      } catch (error) {
        console.error("Failed to fetch account types:", error);
        setAccountTypesLoaded(true); 
      }
    };
  
    fetchAccountTypes();
  }, [agreementPublicId, getAccessToken]);

  const handleDeleteAccountType = async (accountTypePublicId) => {
    const toastId = toast.loading("Deleting account types...");
    try {
      const accessToken = await getAccessToken();
      await deleteAccountType(accountTypePublicId, accessToken);
      const newAccountTypes = await getAccountTypesByAgreement(agreementPublicId, accessToken);
      setAccountTypes(newAccountTypes);
      toast.success("Account type deleted", { id: toastId });
    } catch (error) {
      toast.error("Failed to delete account type", { id: toastId });
      console.error(error);
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Account types"} loading={!accountTypesLoaded} />
            </div>

            <div className={`${!accountTypesLoaded ? "loading" : ""} page-content`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Account types</h2>

                <Link to={`/${agreementPublicId}/account-type/create`} className="btn-primary">
                  <FontAwesomeIcon icon={faPlus} className="mr-2 text-lg" />
                  Create account type
                </Link>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <div className="flex">
                    <button className="btn-outline-primary icon hidden">
                      <FontAwesomeIcon
                        icon={faFilterList}
                        className="mr-2 text-lg"
                      />
                      Filter
                    </button>
                  </div>
                  <div className="flex items-center">
                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"Name, Manager, Email "}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredAccountTypes}
                    selectedRows={selectedAccountType} // Map selectedUsers to selectedRows
                    setSelectedRows={setSelectedAccountType} // Ensure selection updates correctly
                    noCheckbox={true}
                    columns={[
                      { key: "name", label: "Name" },
                      { key: "price", label: "Price" },
                      { key: "status", label: "Status" },
                      { key: "isOnPremAccount", label: "On-prem AD", render: (item) => (item.isOnPremAccount ? "Yes" : "No") },
                      { key: "licenseMpn", label: "Licence MPN", render: (item) => item.licenseMpn || "-" },
                      { key: "comment", label: "Comment" },
                      { key: "updatedBy", label: "Updated by" },
                      { key: "updatedDateTimeUTC", label: "Updated date" },                      
                    ]}
                    primaryKey="accountTypePublicId" // Match this with accountTypePublicId
                    basePath={`/${agreementPublicId}/account-types`} // Match this with the path to edit account type
                    handleDelete={handleDeleteAccountType}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default AccountPage;
